/**
 * Firefox throws `SecurityError: The operation is insecure` from
 * `history.replaceState` / `pushState` inside a sandboxed or third-party
 * iframe (board embed, #15650). Chrome reports the same restriction as
 * `Failed to execute 'replaceState' on 'History'`. Angular's router treats
 * that throw as a rejected navigation promise, which Zone.js then reports
 * as an uncaught error.
 *
 * Swallow only that SecurityError so embed navigation continues without a
 * URL update the host page has forbidden. Other exceptions still propagate.
 */

const GUARD_FLAG = '__insecureHistoryGuard';

function errorText(error: unknown): string {
  if (error == null) {
    return '';
  }
  if (typeof error === 'string') {
    return error;
  }
  if (typeof error !== 'object') {
    return String(error);
  }
  const obj = error as Record<string, unknown>;
  const parts: string[] = [];
  if (typeof obj['name'] === 'string') {
    parts.push(obj['name']);
  }
  if (typeof obj['message'] === 'string') {
    parts.push(obj['message']);
  }
  if (typeof obj['stack'] === 'string') {
    parts.push(obj['stack']);
  }
  const inner = obj['rejection'] ?? obj['ngOriginalError'];
  if (inner != null && inner !== error) {
    parts.push(errorText(inner));
  }
  return parts.join('\n');
}

export function isInsecureHistoryError(error: unknown): boolean {
  const text = errorText(error);
  if (
    text.includes("Failed to execute 'replaceState' on 'History'") ||
    text.includes("Failed to execute 'pushState' on 'History'")
  ) {
    return true;
  }
  return text.includes('SecurityError') && text.includes('The operation is insecure');
}

type HistoryUpdater = History['replaceState'];

function guardMethod(target: History, method: 'replaceState' | 'pushState'): void {
  const current = target[method] as HistoryUpdater & { [GUARD_FLAG]?: boolean };
  if (current[GUARD_FLAG]) {
    return;
  }
  const wrapped = function (this: History, data: unknown, unused: string, url?: string | URL | null): void {
    try {
      current.call(this, data, unused, url);
    } catch (err) {
      if (!isInsecureHistoryError(err)) {
        throw err;
      }
    }
  } as HistoryUpdater & { [GUARD_FLAG]?: boolean };
  wrapped[GUARD_FLAG] = true;
  target[method] = wrapped;
}

export function installInsecureHistoryGuard(target: History = window.history): void {
  guardMethod(target, 'replaceState');
  guardMethod(target, 'pushState');
}
